import { Grid, IconButton } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import AccountBoxOutLineIcon from '@material-ui/icons/AccountBox';
import LockOutLineIcon from '@material-ui/icons/LockOutlined';
import MailOutLineIcon from '@material-ui/icons/MailOutline';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { LoadingButton } from 'components';
import { useAuth } from 'context/AuthContext';
import { useNotifications } from 'context/NotificationsContext';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import { useRegisterJobSeekerMutation } from 'graphql/generated';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import browserHistory from 'utils/browser-utils';
import { useSignupSchema } from './validationSchemas/jobseekerValidationSchema';
import { logAnalyticsEvent } from 'ui-services/analytics.ui-service';
import { GoogleAnaliticsEvent } from 'enum';

const initialValues = {
    firstName: '',
    password: '',
    lastName: '',
    email: '',
};

type JobseekerRegistrationFormProps = {
    classes: any;
    pageUsage?: boolean;
};

export const JobseekerRegistrationForm: React.FC<JobseekerRegistrationFormProps> = ({ classes, pageUsage = false }) => {
    const { t } = useTranslation();
    const { notify } = useNotifications();
    const { setEmail, selectTab } = useAuth();
    const [passwordFieldType, setPasswordFieldType] = useState('password');
    const SignupSchema = useSignupSchema(t);

    const formRef = useRef();

    const toggleShowPassword = () => {
        if (passwordFieldType === 'password') {
            setPasswordFieldType('text');
        } else {
            setPasswordFieldType('password');
        }
    };

    const [signUp, { loading }] = useRegisterJobSeekerMutation({
        onCompleted() {
            logAnalyticsEvent(GoogleAnaliticsEvent.JOBSEEKER_REGISTERED);
            notify({
                type: 'success',
                message: t('jobSeekerRegistration:successAlert'),
            });
            if (pageUsage) {
                browserHistory.push('/sign-in');
            } else {
                selectTab(0);
            }
        },
        onError(error) {
            notify({
                type: 'error',
                message: t(`error:${error.message}`) || `${t('defaultErrorMessage')}`,
            });
        },
    });

    const onSubmit = (formData, { setSubmitting }): void => {
        setEmail(formData.email);
        signUp({
            variables: {
                jobseeker: formData,
            },
        });
        setSubmitting(false);
    };

    return (
        <Formik ref={formRef} initialValues={initialValues} validationSchema={SignupSchema} onSubmit={onSubmit}>
            {({ submitForm }): React.ReactNode => {
                return (
                    <Form className={classes.form}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Field
                                    component={TextField}
                                    variant="outlined"
                                    fullWidth
                                    disabled={loading}
                                    id="firstName"
                                    label={t('employerRegistration:firstName')}
                                    name="firstName"
                                    autoComplete="off"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AccountBoxOutLineIcon className={classes.inputIcon} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Field
                                    component={TextField}
                                    variant="outlined"
                                    fullWidth
                                    disabled={loading}
                                    name="lastName"
                                    label={t('employerRegistration:lastName')}
                                    id="lastName"
                                    autoComplete="off"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AccountBoxOutLineIcon className={classes.inputIcon} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Field
                                    component={TextField}
                                    variant="outlined"
                                    fullWidth
                                    disabled={loading}
                                    name="email"
                                    label={t('employerRegistration:email')}
                                    id="email"
                                    autoComplete="off"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <MailOutLineIcon className={classes.inputIcon} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{
                                        autoCapitalize: 'none',
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Field
                                    component={TextField}
                                    fullWidth
                                    variant="outlined"
                                    name="password"
                                    label={t('employerRegistration:password')}
                                    type={passwordFieldType}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockOutLineIcon className={classes.inputIcon} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <IconButton onClick={toggleShowPassword}>
                                                {passwordFieldType === 'password' ? (
                                                    <VisibilityOffIcon />
                                                ) : (
                                                    <VisibilityIcon />
                                                )}
                                            </IconButton>
                                        ),
                                    }}
                                    inputProps={{
                                        autoCapitalize: 'none',
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <LoadingButton
                            fullWidth
                            type="submit"
                            onClick={() => {
                                submitForm();
                                logAnalyticsEvent(GoogleAnaliticsEvent.REGISTER_BUTTON_CLICKED);
                            }}
                            loading={loading}
                            variant="contained"
                            className={classes.submit}
                            color="primary"
                            loadingText={t('employerRegistration:registering')}
                            text={t('employerRegistration:register')}
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};
